/**
 * BulkAddUserModal.jsx
 *
 * Two methods:
 *  Tab 1 — Form Manual  : add/remove rows in an editable table grid
 *  Tab 2 — Import CSV/Excel : upload file → parse → preview → submit
 */
import { useState, useRef, useCallback } from 'react';
import {
  X, Plus, Trash2, Download, Upload, CheckCircle2, XCircle,
  AlertTriangle, Loader2, Users, FileSpreadsheet, ClipboardList,
  ChevronDown, RefreshCw, Check, Info
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { apiPost, apiFetch } from '../../lib/api';
import { toast } from 'sonner';

const SYSTEM_ROLES = ['admin', 'vendor', 'buyer', 'superadmin', 'production', 'finance', 'management'];

const ROLE_COLORS = {
  superadmin: 'bg-purple-100 text-purple-700',
  admin:      'bg-blue-100   text-blue-700',
  vendor:     'bg-emerald-100 text-emerald-700',
  buyer:      'bg-amber-100  text-amber-700',
  production: 'bg-cyan-100   text-cyan-700',
  finance:    'bg-orange-100 text-orange-700',
  management: 'bg-slate-100  text-slate-700',
};

function makeBlankRow() {
  return {
    _key: Math.random().toString(36).slice(2),
    name: '', email: '', password: 'User@123',
    role: 'admin', status: 'active',
    vendor_id: '', buyer_id: '', customer_name: '',
  };
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BulkAddUserModal({ onClose, onSuccess, vendors = [], buyers = [], customRoles = [] }) {
  const [activeTab, setActiveTab] = useState('manual');

  const allRoles = [...new Set([...SYSTEM_ROLES, ...customRoles.map(r => r.name)])];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-white text-lg leading-tight">Bulk Tambah User</p>
              <p className="text-blue-200 text-xs">Tambahkan banyak user sekaligus (maks. 200)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 text-white transition-colors" data-testid="bulk-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 flex-shrink-0 bg-white px-6">
          {[
            { id: 'manual', label: 'Form Manual', icon: ClipboardList },
            { id: 'csv',    label: 'Import CSV / Excel', icon: FileSpreadsheet },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`bulk-tab-${tab.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'manual' && (
            <ManualTab allRoles={allRoles} vendors={vendors} buyers={buyers} onClose={onClose} onSuccess={onSuccess} />
          )}
          {activeTab === 'csv' && (
            <CsvTab allRoles={allRoles} vendors={vendors} buyers={buyers} onClose={onClose} onSuccess={onSuccess} />
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MANUAL TAB — editable row grid
// ═══════════════════════════════════════════════════════════════════════════════
function ManualTab({ allRoles, vendors, buyers, onClose, onSuccess }) {
  const [rows, setRows] = useState([makeBlankRow(), makeBlankRow(), makeBlankRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const addRow = () => setRows(r => [...r, makeBlankRow()]);
  const removeRow = (key) => setRows(r => r.filter(x => x._key !== key));
  const updateRow = (key, field, value) =>
    setRows(r => r.map(x => x._key === key ? { ...x, [field]: value } : x));

  const filledRows = rows.filter(r => r.name.trim() || r.email.trim());

  const handleSubmit = async () => {
    if (filledRows.length === 0) { toast.error('Isi minimal 1 baris user'); return; }
    setSubmitting(true);
    try {
      const payload = filledRows.map(({ _key, ...r }) => r);
      const data = await apiPost('/users/bulk', { users: payload });
      setResult(data);
      if (data.created > 0) onSuccess?.();
      toast.success(`${data.created} user berhasil ditambahkan${data.failed > 0 ? `, ${data.failed} gagal` : ''}`);
    } catch (e) {
      toast.error(e.message || 'Gagal menambahkan user');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <ResultView result={result} onClose={onClose} onReset={() => { setResult(null); setRows([makeBlankRow()]); }} />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100 bg-slate-50/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">
            <span className="font-semibold text-blue-600">{filledRows.length}</span> / {rows.length} baris terisi
          </span>
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5">
            <Info className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">Baris kosong (tanpa nama & email) akan diabaikan. Password default: <code className="font-mono bg-blue-100 px-1 rounded">User@123</code></p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={addRow}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
            data-testid="add-row-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Tambah Baris
          </button>
          <button
            onClick={() => setRows([makeBlankRow()])}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr className="border-b-2 border-slate-200">
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600 w-8">#</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600 min-w-[160px]">Nama *</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600 min-w-[200px]">Email *</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600 min-w-[130px]">Password</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600 min-w-[120px]">Role</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600 min-w-[90px]">Status</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600 min-w-[140px]">Vendor / Buyer</th>
              <th className="px-2 py-2.5 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row._key} className={`border-b border-slate-100 transition-colors ${row.name || row.email ? 'bg-white' : 'bg-slate-50/30'}`}>
                <td className="px-3 py-2 text-slate-400 text-xs font-mono">{idx + 1}</td>
                <td className="px-2 py-1.5">
                  <input
                    value={row.name}
                    onChange={e => updateRow(row._key, 'name', e.target.value)}
                    placeholder="Nama lengkap"
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                    data-testid={`row-name-${idx}`}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="email"
                    value={row.email}
                    onChange={e => updateRow(row._key, 'email', e.target.value)}
                    placeholder="email@domain.com"
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                    data-testid={`row-email-${idx}`}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="text"
                    value={row.password}
                    onChange={e => updateRow(row._key, 'password', e.target.value)}
                    placeholder="User@123"
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:border-blue-400"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <select
                    value={row.role}
                    onChange={e => updateRow(row._key, 'role', e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 bg-white"
                    data-testid={`row-role-${idx}`}
                  >
                    {allRoles.map(r => (
                      <option key={r} value={r} className="capitalize">{r}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  <select
                    value={row.status}
                    onChange={e => updateRow(row._key, 'status', e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 bg-white"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  {row.role === 'vendor' ? (
                    <select
                      value={row.vendor_id}
                      onChange={e => updateRow(row._key, 'vendor_id', e.target.value)}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-emerald-400 bg-white"
                    >
                      <option value="">— Pilih Vendor —</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>{v.garment_name}</option>
                      ))}
                    </select>
                  ) : row.role === 'buyer' ? (
                    <select
                      value={row.buyer_id}
                      onChange={e => {
                        const b = buyers.find(x => x.id === e.target.value);
                        updateRow(row._key, 'buyer_id', e.target.value);
                        updateRow(row._key, 'customer_name', b?.buyer_name || '');
                      }}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-amber-400 bg-white"
                    >
                      <option value="">— Pilih Buyer —</option>
                      {buyers.map(b => (
                        <option key={b.id} value={b.id}>{b.buyer_name}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-slate-300 text-xs px-2">—</span>
                  )}
                </td>
                <td className="px-1 py-1.5 text-center">
                  {rows.length > 1 && (
                    <button
                      onClick={() => removeRow(row._key)}
                      className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                      title="Hapus baris"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-white flex-shrink-0">
        <button
          onClick={addRow}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          <Plus className="w-4 h-4" /> Tambah baris
        </button>
        <div className="flex gap-3">
          <button onClick={onClose} className="px-4 py-2 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || filledRows.length === 0}
            data-testid="bulk-submit-btn"
            className="flex items-center gap-2 px-6 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            {submitting ? 'Menyimpan...' : `Tambah ${filledRows.length} User`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CSV / EXCEL TAB
// ═══════════════════════════════════════════════════════════════════════════════
function CsvTab({ allRoles, vendors, buyers, onClose, onSuccess }) {
  const fileInputRef = useRef(null);
  const [parsedRows, setParsedRows] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [fileName, setFileName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const res = await apiFetch('/users/bulk/template');
      if (!res.ok) throw new Error('Gagal download template');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'template_bulk_user.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || 'Gagal download template');
    }
  };

  const parseFile = useCallback((file) => {
    setParseError(null);
    setParsedRows(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', codepage: 65001 });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (!json.length) { setParseError('File kosong atau tidak ada data'); return; }

        // Normalize headers (handle BOM, whitespace, case)
        const normalize = (s) => String(s).replace(/^\uFEFF/, '').trim().toLowerCase();

        // Map column names (flexible: 'nama'/'name', 'email', etc.)
        const COL_MAP = {
          name:          ['nama', 'name', 'nama_lengkap', 'full_name'],
          email:         ['email'],
          password:      ['password', 'kata_sandi', 'pass'],
          role:          ['role', 'peran'],
          status:        ['status'],
          vendor_id:     ['vendor_id'],
          buyer_id:      ['buyer_id'],
          customer_name: ['customer_name', 'nama_buyer', 'buyer_name'],
        };

        const firstRow = json[0];
        const headerMap = {};
        Object.entries(COL_MAP).forEach(([field, aliases]) => {
          const matchedKey = Object.keys(firstRow).find(k =>
            aliases.includes(normalize(k))
          );
          if (matchedKey) headerMap[field] = matchedKey;
        });

        if (!headerMap.name || !headerMap.email) {
          setParseError('Kolom wajib tidak ditemukan. Pastikan ada kolom "nama" dan "email".');
          return;
        }

        const rows = json.map((r, idx) => ({
          _key:          String(idx),
          _row:          idx + 2,
          name:          String(r[headerMap.name] || '').trim(),
          email:         String(r[headerMap.email] || '').trim().toLowerCase(),
          password:      String(r[headerMap.password] || 'User@123').trim() || 'User@123',
          role:          String(r[headerMap.role] || 'admin').trim() || 'admin',
          status:        String(r[headerMap.status] || 'active').trim() || 'active',
          vendor_id:     String(r[headerMap.vendor_id] || '').trim(),
          buyer_id:      String(r[headerMap.buyer_id] || '').trim(),
          customer_name: String(r[headerMap.customer_name] || '').trim(),
        })).filter(r => r.name || r.email);

        if (rows.length === 0) { setParseError('Tidak ada baris data yang terisi'); return; }
        if (rows.length > 200) { setParseError('Maksimal 200 baris per upload'); return; }

        setParsedRows(rows);
      } catch (err) {
        setParseError('Gagal membaca file: ' + err.message);
      }
    };
    reader.readAsBinaryString(file);
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) parseFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  };

  const handleSubmit = async () => {
    if (!parsedRows?.length) return;
    setSubmitting(true);
    try {
      const payload = parsedRows.map(({ _key, _row, ...r }) => r);
      const data = await apiPost('/users/bulk', { users: payload });
      setResult(data);
      if (data.created > 0) onSuccess?.();
      toast.success(`${data.created} user berhasil ditambahkan${data.failed > 0 ? `, ${data.failed} gagal` : ''}`);
    } catch (e) {
      toast.error(e.message || 'Gagal menambahkan user');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <ResultView result={result} onClose={onClose} onReset={() => { setResult(null); setParsedRows(null); setFileName(''); }} />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6 space-y-5">

        {/* Download template */}
        <div className="flex items-start justify-between bg-teal-50 border border-teal-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-teal-800">Unduh Template CSV</p>
              <p className="text-xs text-teal-600 mt-0.5">
                Kolom: <code className="bg-teal-100 px-1 rounded font-mono text-xs">nama, email, password, role, status, vendor_id, buyer_id, customer_name</code>
              </p>
            </div>
          </div>
          <button
            onClick={handleDownloadTemplate}
            data-testid="download-template-btn"
            className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-semibold hover:bg-teal-700 transition-colors flex-shrink-0"
          >
            <Download className="w-3.5 h-3.5" /> Template
          </button>
        </div>

        {/* Upload zone */}
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Upload File</p>
          <div
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            data-testid="csv-drop-zone"
            className={`flex flex-col items-center justify-center w-full h-36 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
              fileName
                ? 'border-blue-400 bg-blue-50'
                : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/30'
            }`}
          >
            <Upload className={`w-8 h-8 mb-2 ${fileName ? 'text-blue-500' : 'text-slate-400'}`} />
            <p className="text-sm text-slate-600">
              {fileName
                ? <span className="text-blue-600 font-semibold">{fileName}</span>
                : <><span className="font-medium">Klik</span> atau drag & drop file</>
              }
            </p>
            <p className="text-xs text-slate-400 mt-1">Mendukung .csv, .xlsx, .xls — maks. 200 baris</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={handleFileChange}
            data-testid="csv-file-input"
          />
        </div>

        {/* Parse error */}
        {parseError && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{parseError}</p>
          </div>
        )}

        {/* Preview table */}
        {parsedRows && !parseError && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <p className="text-sm font-semibold text-green-700">{parsedRows.length} baris siap diimport dari <span className="font-mono text-xs">{fileName}</span></p>
              </div>
              <button
                onClick={() => { setParsedRows(null); setFileName(''); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="text-xs text-slate-400 hover:text-red-500 underline"
              >Ganti file</button>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto max-h-64">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      {['#', 'Nama', 'Email', 'Password', 'Role', 'Status'].map(h => (
                        <th key={h} className="text-left px-3 py-2 font-semibold text-slate-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {parsedRows.map((r, i) => (
                      <tr key={r._key} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 text-slate-400">{r._row}</td>
                        <td className="px-3 py-1.5 font-medium text-slate-800">{r.name || <span className="text-red-400 italic">kosong</span>}</td>
                        <td className="px-3 py-1.5 text-slate-600">{r.email || <span className="text-red-400 italic">kosong</span>}</td>
                        <td className="px-3 py-1.5 font-mono text-slate-400">{r.password ? '••••••' : <span className="text-amber-500">default</span>}</td>
                        <td className="px-3 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[r.role] || 'bg-indigo-100 text-indigo-700'}`}>
                            {r.role}
                          </span>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-white flex-shrink-0">
        <button onClick={onClose} className="px-4 py-2 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
          Batal
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || !parsedRows?.length}
          data-testid="csv-submit-btn"
          className="flex items-center gap-2 px-6 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {submitting ? 'Mengimport...' : `Import ${parsedRows?.length || 0} User`}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// RESULT VIEW (shared between tabs)
// ═══════════════════════════════════════════════════════════════════════════════
function ResultView({ result, onClose, onReset }) {
  const successRows = result.results.filter(r => r.success);
  const failedRows  = result.results.filter(r => !r.success);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6 space-y-5">

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Dikirim', value: result.total, cls: 'bg-slate-50 border-slate-200 text-slate-700' },
            { label: 'Berhasil Ditambah', value: result.created, cls: 'bg-green-50 border-green-200 text-green-700', icon: CheckCircle2 },
            { label: 'Gagal', value: result.failed, cls: result.failed > 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-slate-50 border-slate-200 text-slate-500', icon: result.failed > 0 ? XCircle : null },
          ].map((card, i) => (
            <div key={i} className={`flex items-center gap-3 border rounded-xl p-4 ${card.cls}`}>
              {card.icon && <card.icon className="w-5 h-5 flex-shrink-0" />}
              <div>
                <p className="text-2xl font-bold">{card.value}</p>
                <p className="text-xs font-medium opacity-75 mt-0.5">{card.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Success list */}
        {successRows.length > 0 && (
          <div className="border border-green-200 rounded-xl overflow-hidden">
            <div className="bg-green-50 px-4 py-2.5 border-b border-green-200">
              <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> {successRows.length} User Berhasil Ditambahkan
              </p>
            </div>
            <div className="divide-y divide-green-100 max-h-48 overflow-y-auto">
              {successRows.map(r => (
                <div key={r.row} className="flex items-center gap-3 px-4 py-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${ROLE_COLORS[r.role] ? '' : 'bg-blue-600'}`}
                    style={{ background: r.role === 'vendor' ? '#059669' : r.role === 'buyer' ? '#d97706' : r.role === 'admin' ? '#2563eb' : '#7c3aed' }}>
                    {r.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                    <p className="text-xs text-slate-500 truncate">{r.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[r.role] || 'bg-indigo-100 text-indigo-700'}`}>
                    {r.role}
                  </span>
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Failed list */}
        {failedRows.length > 0 && (
          <div className="border border-red-200 rounded-xl overflow-hidden">
            <div className="bg-red-50 px-4 py-2.5 border-b border-red-200">
              <p className="text-sm font-semibold text-red-800 flex items-center gap-2">
                <XCircle className="w-4 h-4" /> {failedRows.length} User Gagal
              </p>
            </div>
            <div className="divide-y divide-red-100 max-h-48 overflow-y-auto">
              {failedRows.map(r => (
                <div key={r.row} className="flex items-start gap-3 px-4 py-2.5">
                  <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs text-red-600 font-bold">{r.row}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.email || '—'}</p>
                    <p className="text-xs text-red-600 mt-0.5">{r.error}</p>
                  </div>
                  <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-white flex-shrink-0">
        <button
          onClick={onReset}
          className="flex items-center gap-2 px-4 py-2 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <Plus className="w-4 h-4" /> Tambah Lagi
        </button>
        <button
          onClick={onClose}
          data-testid="bulk-result-close-btn"
          className="px-6 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all"
        >
          Selesai
        </button>
      </div>
    </div>
  );
}
